import getEnv from '@/utils/env'

const authenticationRedirectPendingKey = 'fransys_authentication_redirect_pending'
let authenticationRedirectStarted = false

const jsonApiType = 'application/vnd.api+json'
const csrfErrorDetail = 'CSRF proof is missing or invalid'

function authenticationApiUrl(path: string): string {
  const baseUrl = getEnv('VUE_APP_AUTHENTICATION_API_URL')

  if (!baseUrl) {
    throw new Error('VUE_APP_AUTHENTICATION_API_URL is required')
  }

  return new URL(`/v1${path}`, `${baseUrl.replace(/\/$/, '')}/`).toString()
}

// The authentication API issues its CSRF proof in this header on every response, because this
// frontend's host cannot read the API's cookies.
function csrfProof(response: Response): string | undefined {
  return response.headers.get('X-CSRF-Token') || legacyCookieProof()
}

// TEMPORARY: backends up to release 1.1.1 send no proof header and set this readable cookie
// instead. Revert the commit that added this once production runs a later backend.
function legacyCookieProof(): string | undefined {
  const cookieName = 'fransys_csrf_proof'

  return window.document.cookie.split(';').map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith(`${cookieName}=`))
    ?.slice(cookieName.length + 1)
}

function getFromAuthenticationApi(path: string): Promise<Response> {
  return fetch(authenticationApiUrl(path), {
    headers: { Accept: jsonApiType },
    credentials: 'include',
  })
}

function selectOrganisation(organisationId: string, proof: string): Promise<Response> {
  return fetch(authenticationApiUrl('/sessions/current'), {
    method: 'PATCH',
    headers: {
      Accept: jsonApiType,
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': proof,
    },
    credentials: 'include',
    body: JSON.stringify({ organisation_id: organisationId }),
  })
}

async function isCsrfRejection(response: Response): Promise<boolean> {
  if (response.status !== 403) {
    return false
  }

  const body = await response.clone().json().catch(() => null) as {
    errors?: Array<{ detail?: string }>
  } | null

  return body?.errors?.some(error => error.detail === csrfErrorDetail) ?? false
}

/** Returns the organisation already selected on the session, if any. */
async function fetchSelectedOrganisationId(): Promise<string | undefined> {
  const response = await getFromAuthenticationApi('/organisation')

  if (response.status === 401 || response.status === 403) {
    throw new Error(`Unable to load the selected organisation (${response.status})`)
  }
  if (!response.ok) {
    return undefined
  }

  const id = (await response.json() as { data?: { id?: unknown } }).data?.id

  return typeof id === 'string' && id.length > 0 ? id : undefined
}

/** Selects the identity's first organisation on the session. */
async function selectFirstOrganisation(): Promise<string> {
  const response = await getFromAuthenticationApi('/organisations')

  if (!response.ok) {
    throw new Error(`Unable to load organisations (${response.status})`)
  }

  const id = (await response.json() as { data?: Array<{ id?: unknown }> }).data?.[0]?.id

  if (typeof id !== 'string' || id.length === 0) {
    throw new Error('The authenticated identity does not belong to an organisation')
  }

  const proof = csrfProof(response)
  if (!proof) {
    throw new Error('The authentication service did not issue a CSRF proof')
  }

  let selection = await selectOrganisation(id, proof)

  // The refusal carries a proof for the session as the API now sees it: retry once with it.
  if (await isCsrfRejection(selection)) {
    const refreshedProof = csrfProof(selection)
    if (refreshedProof && refreshedProof !== proof) {
      selection = await selectOrganisation(id, refreshedProof)
    }
  }

  if (!selection.ok) {
    throw new Error(`Unable to select organisation (${selection.status})`)
  }

  return id
}

let activeOrganisationPromise: Promise<string> | null = null

/** Resolves the organisation selected on the session, selecting one first if needed. Shared between callers. */
export function activeOrganisationId(): Promise<string> {
  activeOrganisationPromise ??= (async () => (
    await fetchSelectedOrganisationId() ?? await selectFirstOrganisation()
  ))().catch((error) => {
    activeOrganisationPromise = null
    throw error
  })

  return activeOrganisationPromise
}

function portalUrl(path: string, returnTo?: string): string {
  const baseUrl = getEnv('VUE_APP_AUTH_PORTAL_URL')

  if (!baseUrl) {
    throw new Error('VUE_APP_AUTH_PORTAL_URL is required')
  }

  const url = new URL(path, `${baseUrl.replace(/\/$/, '')}/`)
  if (returnTo) {
    url.searchParams.set('returnTo', returnTo)
  }

  return url.toString()
}

export function redirectToLogin(returnTo = window.location.href): void {
  if (authenticationRedirectStarted) {
    return
  }

  authenticationRedirectStarted = true

  if (window.sessionStorage.getItem(authenticationRedirectPendingKey) === 'true') {
    const errorUrl = new URL('/authentication-error', window.location.origin)
    errorUrl.searchParams.set('status', '401')
    errorUrl.searchParams.set('reason', 'session-not-accepted')
    window.location.assign(errorUrl)
    return
  }

  window.sessionStorage.setItem(authenticationRedirectPendingKey, 'true')
  window.location.assign(portalUrl('/login', returnTo))
}

export function clearAuthenticationRedirect(): void {
  authenticationRedirectStarted = false
  window.sessionStorage.removeItem(authenticationRedirectPendingKey)
}

export function redirectToLogout(): void {
  clearAuthenticationRedirect()
  window.location.assign(portalUrl('/logout'))
}
